import React, { useEffect, useMemo, useState } from 'react';
import { FaTimes, FaTrash } from 'react-icons/fa';
import { addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { PUBLIC_RACE_TYPES } from '../utils/raceTypes';

const overlayBaseStyle = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 20000,
};

const modalStyle = {
  backgroundColor: '#0f0f0f',
  color: '#e0e0e0',
  padding: '18px',
  borderRadius: '10px',
  border: '1px solid #2a2a2a',
  width: '92vw',
  maxWidth: '900px',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6)',
};

const actionBtnStyle = {
  background: 'transparent',
  border: '1px solid #2a2a2a',
  color: '#e0e0e0',
  borderRadius: '8px',
  padding: '10px 12px',
  cursor: 'pointer',
  fontWeight: 700,
};

const iconBtnStyle = {
  ...actionBtnStyle,
  padding: '8px 10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const primaryBtnStyle = {
  ...actionBtnStyle,
  background: 'var(--brand-accent-gradient)',
  border: 'none',
  color: '#fff',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: '1px solid #2a2a2a',
  background: '#111',
  color: '#e0e0e0',
  outline: 'none',
  boxSizing: 'border-box',
};

function toNumberOrNull(v) {
  if (v === '' || v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function RaceTypeManagerModal({ isOpen, onClose, userId, onChanged }) {
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userRaceTypes, setUserRaceTypes] = useState([]);

  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [effortMode, setEffortMode] = useState('percent');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [z1, setZ1] = useState('');
  const [z2, setZ2] = useState('');
  const [z3, setZ3] = useState('');
  const [z4, setZ4] = useState('');
  const [z5, setZ5] = useState('');

  const overlayStyle = useMemo(() => {
    return {
      ...overlayBaseStyle,
      left: sidebarWidth ? `${sidebarWidth}px` : 0,
      width: sidebarWidth ? `calc(100% - ${sidebarWidth}px)` : '100%',
    };
  }, [sidebarWidth]);

  const publicList = useMemo(() => PUBLIC_RACE_TYPES, []);

  const isEditingPreset = useMemo(
    () => editingId != null && publicList.some((p) => p.id === editingId),
    [editingId, publicList]
  );

  const sortedUserRaceTypes = useMemo(() => {
    const list = Array.isArray(userRaceTypes) ? [...userRaceTypes] : [];
    list.sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));
    return list;
  }, [userRaceTypes]);

  useEffect(() => {
    if (!isOpen) return;
    const measure = () => {
      const el = document.querySelector('.sidebar');
      const w = el && el.getBoundingClientRect ? el.getBoundingClientRect().width : 0;
      setSidebarWidth(Number.isFinite(w) ? w : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !userId) return;
    void loadRaceTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userId]);

  async function loadRaceTypes() {
    if (!userId) return;
    try {
      setLoading(true);
      const q = query(collection(db, 'raceTypes'), where('userId', '==', userId));
      const snap = await getDocs(q);
      setUserRaceTypes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Failed to load race types:', e);
      alert('Failed to load race types. Please check console.');
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setEditingId(null);
    setName('');
    setEffortMode('percent');
    setDurationMinutes('');
    setZ1('');
    setZ2('');
    setZ3('');
    setZ4('');
    setZ5('');
  }

  function loadForEdit(rt) {
    setEditingId(rt?.id || null);
    setName(rt?.name || '');
    setEffortMode(rt?.defaults?.effortMode === 'minutes' ? 'minutes' : 'percent');
    setDurationMinutes(rt?.defaults?.durationMinutes != null ? String(rt.defaults.durationMinutes) : '');
    setZ1(rt?.defaults?.zoneMix?.z1 != null ? String(rt.defaults.zoneMix.z1) : '');
    setZ2(rt?.defaults?.zoneMix?.z2 != null ? String(rt.defaults.zoneMix.z2) : '');
    setZ3(rt?.defaults?.zoneMix?.z3 != null ? String(rt.defaults.zoneMix.z3) : '');
    setZ4(rt?.defaults?.zoneMix?.z4 != null ? String(rt.defaults.zoneMix.z4) : '');
    setZ5(rt?.defaults?.zoneMix?.z5 != null ? String(rt.defaults.zoneMix.z5) : '');
  }

  async function duplicatePublicType(publicRt) {
    if (!userId) return;
    const defaults = publicRt.defaults
      ? {
          effortMode: publicRt.defaults.effortMode || 'percent',
          durationMinutes: toNumberOrNull(publicRt.defaults.durationMinutes),
          zoneMix: {
            z1: toNumberOrNull(publicRt.defaults.zoneMix?.z1),
            z2: toNumberOrNull(publicRt.defaults.zoneMix?.z2),
            z3: toNumberOrNull(publicRt.defaults.zoneMix?.z3),
            z4: toNumberOrNull(publicRt.defaults.zoneMix?.z4),
            z5: toNumberOrNull(publicRt.defaults.zoneMix?.z5),
          },
        }
      : null;
    const payload = {
      userId,
      name: (publicRt.name || 'Copy').trim(),
      defaults,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    try {
      setSaving(true);
      const ref = await addDoc(collection(db, 'raceTypes'), payload);
      await loadRaceTypes();
      onChanged?.();
      const created = { id: ref.id, ...payload };
      loadForEdit(created);
    } catch (e) {
      console.error('Failed to duplicate race type:', e);
      alert('Failed to duplicate. Please check console.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    if (!userId) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      alert('Race type name is required.');
      return;
    }

    const defaults = {
      effortMode,
      durationMinutes: toNumberOrNull(durationMinutes),
      zoneMix: {
        z1: toNumberOrNull(z1),
        z2: toNumberOrNull(z2),
        z3: toNumberOrNull(z3),
        z4: toNumberOrNull(z4),
        z5: toNumberOrNull(z5),
      },
    };

    // If all defaults are null/empty, store defaults=null
    const hasAnyDefaults =
      defaults.durationMinutes != null ||
      Object.values(defaults.zoneMix).some((v) => v != null);
    const payload = {
      userId,
      name: trimmedName,
      defaults: hasAnyDefaults ? defaults : null,
      updatedAt: Date.now(),
    };

    try {
      setSaving(true);
      const isPreset = publicList.some((p) => p.id === editingId);
      if (editingId && !isPreset) {
        await updateDoc(doc(db, 'raceTypes', editingId), payload);
      } else {
        await addDoc(collection(db, 'raceTypes'), {
          ...payload,
          createdAt: Date.now(),
        });
      }
      await loadRaceTypes();
      onChanged?.();
      resetForm();
    } catch (e) {
      console.error('Failed to save race type:', e);
      alert('Failed to save race type. Please check console.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!editingId) return;
    const ok = window.confirm('Delete this race type?');
    if (!ok) return;
    try {
      setSaving(true);
      await deleteDoc(doc(db, 'raceTypes', editingId));
      await loadRaceTypes();
      onChanged?.();
      resetForm();
    } catch (e) {
      console.error('Failed to delete race type:', e);
      alert('Failed to delete race type. Please check console.');
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <div style={{ fontSize: '18px', fontWeight: 800 }}>Race Types</div>
          <button type="button" style={iconBtnStyle} onClick={onClose} aria-label="Close" title="Close">
            <FaTimes />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginTop: '14px' }}>
          <div>
            <div style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>
              Presets — click to edit, then save as your type
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {publicList.map((rt) => (
                <div
                  key={rt.id}
                  style={{
                    padding: '10px 12px',
                    background: editingId === rt.id ? 'rgba(var(--brand-accent-rgb), 0.18)' : '#151515',
                    border: editingId === rt.id ? '1px solid rgba(var(--brand-accent-rgb), 0.8)' : '1px solid #2a2a2a',
                    borderRadius: '10px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => loadForEdit(rt)}
                  onKeyDown={(e) => e.key === 'Enter' && loadForEdit(rt)}
                  role="button"
                  tabIndex={0}
                  title="Click to edit in the form, then save as your type"
                >
                  <div>
                    <div style={{ fontWeight: 800 }}>{rt.name}</div>
                    <div style={{ fontSize: '12px', color: '#9aa0a6', marginTop: '4px' }}>
                      {rt.defaults?.durationMinutes ? `${rt.defaults.durationMinutes} min` : 'No defaults'}
                      {rt.defaults?.zoneMix ? ` · Z4 ${rt.defaults.zoneMix.z4}% Z5 ${rt.defaults.zoneMix.z5}%` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    style={{ ...actionBtnStyle, flexShrink: 0, fontSize: '12px' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      duplicatePublicType(rt);
                    }}
                    disabled={saving}
                    title="Copy to your race types"
                  >
                    Duplicate
                  </button>
                </div>
              ))}
            </div>

            <div style={{ fontSize: '13px', color: '#888', marginTop: '14px', marginBottom: '8px' }}>
              Your race types — click to edit
            </div>
            {loading ? (
              <div style={{ color: '#9aa0a6' }}>Loading…</div>
            ) : sortedUserRaceTypes.length === 0 ? (
              <div style={{ color: '#9aa0a6' }}>None. Duplicate a preset above or create one.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {sortedUserRaceTypes.map((rt) => (
                  <button
                    key={rt.id}
                    type="button"
                    onClick={() => loadForEdit(rt)}
                    style={{
                      textAlign: 'left',
                      padding: '10px 12px',
                      background: editingId === rt.id ? 'rgba(var(--brand-accent-rgb), 0.18)' : '#151515',
                      border: editingId === rt.id ? '1px solid rgba(var(--brand-accent-rgb), 0.8)' : '1px solid #2a2a2a',
                      borderRadius: '10px',
                      color: '#e0e0e0',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ fontWeight: 800 }}>{rt.name}</div>
                    <div style={{ fontSize: '12px', color: '#9aa0a6', marginTop: '4px' }}>
                      {rt.defaults?.durationMinutes ? `${rt.defaults.durationMinutes} min` : 'No defaults'}
                      {rt.defaults?.zoneMix ? ` · Z4 ${rt.defaults.zoneMix.z4}% Z5 ${rt.defaults.zoneMix.z5}%` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
              <div style={{ fontSize: '13px', color: '#888' }}>
                {isEditingPreset ? 'Edit preset (save as your type)' : editingId ? 'Edit' : 'Create'} race type
              </div>
              {editingId && !isEditingPreset ? (
                <button type="button" style={iconBtnStyle} onClick={handleDelete} disabled={saving} title="Delete">
                  <FaTrash />
                </button>
              ) : null}
            </div>

            <div style={{ marginTop: '10px' }}>
              <label style={{ display: 'block', fontSize: '13px', color: '#888', marginBottom: '6px' }}>
                Name
              </label>
              <input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} disabled={saving} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: '#888', marginBottom: '6px' }}>
                  Effort mode
                </label>
                <select style={inputStyle} value={effortMode} onChange={(e) => setEffortMode(e.target.value)} disabled={saving}>
                  <option value="percent">Percent</option>
                  <option value="minutes">Minutes</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', color: '#888', marginBottom: '6px' }}>
                  Default duration (min)
                </label>
                <input
                  style={inputStyle}
                  type="number"
                  min="0"
                  step="1"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(e.target.value)}
                  disabled={saving}
                />
              </div>
            </div>

            <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
              {[
                { label: 'Z1', v: z1, set: setZ1 },
                { label: 'Z2', v: z2, set: setZ2 },
                { label: 'Z3', v: z3, set: setZ3 },
                { label: 'Z4', v: z4, set: setZ4 },
                { label: 'Z5', v: z5, set: setZ5 },
              ].map((z) => (
                <div key={z.label}>
                  <label style={{ display: 'block', fontSize: '12px', color: '#888', marginBottom: '6px' }}>
                    {z.label}
                  </label>
                  <input
                    style={inputStyle}
                    type="number"
                    min="0"
                    step="1"
                    value={z.v}
                    onChange={(e) => z.set(e.target.value)}
                    disabled={saving}
                  />
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '14px' }}>
              <button type="button" style={actionBtnStyle} onClick={resetForm} disabled={saving}>
                Clear
              </button>
              <button type="button" style={primaryBtnStyle} onClick={handleSave} disabled={saving || !name.trim()}>
                {saving ? 'Saving…' : isEditingPreset ? 'Save as my type' : editingId ? 'Save Changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

